import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { DATE_RANGE_PRESETS, DEFAULT_RANGE } from "../lib/dateRangePresets.js";
import { MAX_RANGE_DAYS, makeCustomRange, rangeForDays, rangeSpan, spanWords } from "../lib/shell.js";
import { phAttr } from "../lib/analytics/attrs.js";
import { ACTION, ATTRIBUTE } from "../lib/analytics/surfaceVocabulary.js";
import { ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import { buttonClass } from "./ui/Button.jsx";
import { FIELD as UI_FIELD, ICON_BUTTON, MENU, MENU_ITEM, MENU_ITEM_ON, TRIGGER } from "./ui/styles.js";

export function ThemeToggle({ theme, onToggle, children }) {
  return (
    <button
      className={`${ICON_BUTTON} [&_svg]:block [&_svg]:h-3 [&_svg]:w-3`}
      type="button"
      aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
      onClick={onToggle}
    >
      {children}
    </button>
  );
}

// The range picker: a button naming the range, and under it the presets, two
// months to pick days on (after the first pick the band follows the pointer
// to the second), and the range's first and last minute to type or adjust,
// applied together. A preset applies at once. Days after today, and once a
// start is picked, days MAX_RANGE_DAYS or more from it, can't be picked.
// With `anyTime`, "Any time" leads the presets and picks a null range, which
// the button names; the months then open on the default range.
const DAY = 86_400_000;
const PANEL_W = 600;
const MONTH = new Intl.DateTimeFormat(undefined, { month: "long", year: "numeric" });
const FULL_DAY = new Intl.DateTimeFormat(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" });
const WEEK = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];
const QUIET = buttonClass("ghost", "sm");
const PRIMARY = buttonClass("primary", "sm");
const FIELD = `${UI_FIELD} h-[30px] text-[12.5px]`;

const pad = (n) => String(n).padStart(2, "0");
const dayOf = (ms) => { const d = new Date(ms); d.setHours(0, 0, 0, 0); return d.getTime(); };
const lastMinuteOf = (day) => { const d = new Date(day); d.setHours(23, 59, 0, 0); return d.getTime(); };
const daysApart = (a, b) => Math.round(Math.abs(dayOf(a) - dayOf(b)) / DAY);
const firstOfMonth = (ms, by = 0) => { const d = new Date(ms); d.setHours(0, 0, 0, 0); d.setDate(1); d.setMonth(d.getMonth() + by); return d.getTime(); };
const whole = (from, to) => { const s = new Date(from); const e = new Date(to); return s.getHours() === 0 && s.getMinutes() === 0 && e.getHours() === 23 && e.getMinutes() === 59; };
// A minute as a datetime-local field holds it, and back.
const fieldOf = (ms) => { const d = new Date(ms); return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`; };
const minuteOf = (value) => { const ms = value ? new Date(value).getTime() : NaN; return Number.isFinite(ms) ? ms : null; };

// How long [from, to] is (to its last minute's start): whole days by their
// count, else hours, or minutes under an hour.
export function lengthOf(from, to) {
  if (whole(from, to)) { const n = daysApart(from, to) + 1; return `${n} day${n === 1 ? "" : "s"}`; }
  const minutes = Math.round((to + 60_000 - from) / 60_000);
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? "" : "s"}`;
  const hours = Math.round(minutes / 60);
  return hours < 72 ? `${hours} hour${hours === 1 ? "" : "s"}` : `${Math.round(hours / 24)} days`;
}

// One month's days, Monday first. `band` is the days picked, or while the end
// is being picked, the days from the start to the pointer (`preview`).
function Month({ first, band, preview, start, today, onPick, onHover, className = "" }) {
  const head = new Date(first);
  const lead = (head.getDay() + 6) % 7;
  const count = new Date(head.getFullYear(), head.getMonth() + 1, 0).getDate();
  const days = Array.from({ length: count }, (_, i) => new Date(head.getFullYear(), head.getMonth(), i + 1).getTime());
  return (
    <div className={`grid content-start gap-[4px] ${className}`} data-date-month={`${head.getFullYear()}-${pad(head.getMonth() + 1)}`}>
      <div className="flex h-[26px] items-center justify-center text-[12.5px] font-medium">{MONTH.format(head)}</div>
      <div className="grid grid-cols-7 text-center text-[11px] text-[color:var(--muted-soft)]">{WEEK.map((w) => <span key={w}>{w}</span>)}</div>
      <div className="grid grid-cols-7 gap-y-[2px]" onMouseLeave={() => onHover(null)}>
        {Array.from({ length: lead }, (_, i) => <span key={`lead${i}`} />)}
        {days.map((day) => {
          const off = day > today || (start != null && daysApart(day, start) >= MAX_RANGE_DAYS);
          const inBand = band && day >= band[0] && day <= band[1];
          const end = band && (day === band[0] || day === band[1]);
          const tone = off ? "cursor-default text-[color:var(--muted-soft)] opacity-50"
            : end ? "rounded-[6px] bg-[color:var(--green)] font-medium text-[color:var(--bg)]"
            : inBand ? (preview ? "bg-[color-mix(in_srgb,var(--ink)_6%,transparent)]" : "bg-[color-mix(in_srgb,var(--ink)_10%,transparent)]")
            : "rounded-[6px] hover:bg-[color-mix(in_srgb,var(--ink)_8%,transparent)]";
          return (
            <button
              key={day}
              type="button"
              tabIndex={-1}
              disabled={off}
              aria-label={FULL_DAY.format(day)}
              aria-pressed={end ? true : undefined}
              aria-current={day === today ? "date" : undefined}
              title={off && day <= today ? `Up to ${MAX_RANGE_DAYS} days at a time` : undefined}
              onClick={() => onPick(day)}
              onMouseEnter={() => onHover(day)}
              className={`relative h-[28px] text-[12px] tabular-nums ${tone} ${day === today ? "after:absolute after:bottom-[3px] after:left-1/2 after:h-[3px] after:w-[3px] after:-translate-x-1/2 after:rounded-full after:bg-current" : ""}`}
              data-date-day={fieldOf(day).slice(0, 10)}
            >
              {new Date(day).getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** A range as the picker names it: its preset's words, else its dates. */
export function rangeLabel(range) {
  const preset = DATE_RANGE_PRESETS.find((p) => p.id === range);
  if (preset) return preset.summary;
  const { from, to } = rangeSpan(range);
  return spanWords(from, to);
}

export default function DateRangePicker({ range, onRangeChange, compact = false, anyTime = false }) {
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const [open, setOpen] = useState(false);
  // The menu is portalled to <body> with fixed coordinates from the trigger,
  // so no clipping or transformed ancestor can cut or move it.
  const [pos, setPos] = useState({ left: 0, top: 0, width: PANEL_W });
  // The range being picked: its first and last minute (ms); `to` is null
  // while the end is being picked on the months.
  const [draft, setDraft] = useState(null);
  const [hover, setHover] = useState(null);
  // The right-hand month's first day; the left-hand one is the month before.
  const [month, setMonth] = useState(0);
  const [today, setToday] = useState(() => dayOf(Date.now()));

  const any = anyTime && !range;
  const presets = anyTime ? [{ id: null, summary: "Any time" }, ...DATE_RANGE_PRESETS] : DATE_RANGE_PRESETS;
  const preset = presets.find((p) => p.id === (any ? null : range));
  const label = preset ? preset.summary : rangeLabel(range);

  const place = useCallback(() => {
    const r = triggerRef.current?.getBoundingClientRect();
    if (!r) return;
    const width = Math.min(PANEL_W, window.innerWidth - 16);
    // Under the trigger, its left edge with the trigger's, or where that
    // would run off the window, its right edge.
    const left = r.left + width <= window.innerWidth - 8 ? r.left : Math.max(8, r.right - width);
    setPos({ left, top: r.bottom + 4, width });
  }, []);

  const show = () => {
    const { from, to } = rangeSpan(any ? DEFAULT_RANGE : range);
    const last = to - 59_999;
    setDraft({ from, to: last });
    setHover(null);
    setToday(dayOf(Date.now()));
    setMonth(firstOfMonth(last));
    setOpen(true);
  };
  const close = useCallback(() => {
    setOpen(false);
    triggerRef.current?.focus();
  }, []);

  useLayoutEffect(() => {
    if (open) place();
  }, [open, place]);

  useEffect(() => {
    if (!open) return;
    (menuRef.current?.querySelector("[data-date-preset][aria-pressed='true']") || menuRef.current?.querySelector("[data-date-preset]"))?.focus();
    const onMove = () => place();
    const onOutside = (e) => {
      if (triggerRef.current?.contains(e.target) || menuRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") close(); };
    window.addEventListener("scroll", onMove, true);
    window.addEventListener("resize", onMove);
    window.addEventListener("mousedown", onOutside);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("scroll", onMove, true);
      window.removeEventListener("resize", onMove);
      window.removeEventListener("mousedown", onOutside);
      window.removeEventListener("keydown", onKey);
    };
  }, [open, place, close]);

  const pending = draft?.to == null;
  const start = draft && pending ? dayOf(draft.from) : null;
  const band = !draft ? null
    : pending ? [Math.min(hover ?? start, start), Math.max(hover ?? start, start)]
    : [dayOf(draft.from), dayOf(draft.to)];
  const valid = !!draft && !pending && draft.from <= draft.to && daysApart(draft.from, draft.to) < MAX_RANGE_DAYS;

  // A day starts a new range, or ends the one started, whichever way it lies.
  const pick = (day) => setDraft((d) => (d.to != null ? { from: day, to: null }
    : { from: Math.min(day, dayOf(d.from)), to: lastMinuteOf(Math.max(day, dayOf(d.from))) }));
  const type = (key) => (e) => {
    const ms = minuteOf(e.target.value);
    if (ms != null) setDraft((d) => ({ ...d, [key]: ms }));
  };
  const choose = (id) => {
    onRangeChange(id);
    close();
  };
  // Whole days are a preset where they are one; else the minutes picked.
  const apply = () => {
    if (!valid) return;
    const { from, to } = draft;
    onRangeChange(whole(from, to) ? rangeForDays(new Date(dayOf(from)), new Date(dayOf(to))) : makeCustomRange(new Date(from), new Date(to), { exact: true }));
    close();
  };

  return (
    <div data-date-picker className="relative min-w-0">
      <button
        ref={triggerRef}
        type="button"
        data-date-trigger
        data-ph-capture-attribute-action={phAttr(ATTRIBUTE.ACTION, ACTION.RANGE_PICKER)}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => (open ? close() : show())}
        className={`${TRIGGER} w-full min-w-0 ${compact ? "!min-h-[28px] text-[12.5px]" : ""}`}
      >
        <span className="min-w-0 flex-1 truncate" data-date-label="">{label}</span>
        <ChevronDownIcon aria-hidden="true" className={`h-[14px] w-[14px] shrink-0 text-[color:var(--muted-soft)] transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && draft ? createPortal(
        <div
          ref={menuRef}
          role="dialog"
          aria-label="Date range"
          data-date-menu
          style={{ position: "fixed", left: pos.left, top: pos.top, width: pos.width }}
          className={`z-[1000] grid overflow-hidden ${MENU}`}
        >
          <div className="grid min-[560px]:grid-cols-[124px_minmax(0,1fr)]">
            <div role="group" aria-label="Presets" className="flex gap-[2px] overflow-x-auto border-b border-[color:var(--line)] p-[6px] min-[560px]:grid min-[560px]:content-start min-[560px]:border-b-0 min-[560px]:border-r">
              {presets.map((p) => (
                <button
                  key={p.id ?? "any"}
                  type="button"
                  aria-pressed={p === preset}
                  onClick={() => choose(p.id)}
                  className={`${MENU_ITEM} shrink-0 whitespace-nowrap text-[12.5px] ${p === preset ? MENU_ITEM_ON : ""}`}
                  data-date-preset={p.id ?? "any"}
                >
                  {p.summary}
                </button>
              ))}
            </div>
            <div className="relative grid grid-cols-1 gap-[16px] p-[10px] min-[560px]:grid-cols-2">
              <button type="button" aria-label="Earlier month" onClick={() => setMonth((m) => firstOfMonth(m, -1))} className={`${ICON_BUTTON} absolute left-[10px] top-[8px]`} data-date-nav="earlier">
                <ChevronLeftIcon aria-hidden="true" className="h-[14px] w-[14px]" />
              </button>
              <button type="button" aria-label="Later month" disabled={month >= firstOfMonth(today)} onClick={() => setMonth((m) => firstOfMonth(m, 1))} className={`${ICON_BUTTON} absolute right-[10px] top-[8px]`} data-date-nav="later">
                <ChevronRightIcon aria-hidden="true" className="h-[14px] w-[14px]" />
              </button>
              {[firstOfMonth(month, -1), month].map((first, i) => (
                <Month
                  key={first}
                  first={first}
                  band={band}
                  preview={pending}
                  start={start}
                  today={today}
                  onPick={pick}
                  onHover={setHover}
                  className={i === 0 ? "max-[559px]:hidden" : ""}
                />
              ))}
            </div>
          </div>
          {/* The minutes, then what applies them: where both don't fit on a
              line, the second goes under the first, to the right. */}
          <div className="flex flex-wrap items-center gap-x-[12px] gap-y-[8px] border-t border-[color:var(--line)] px-[10px] py-[8px] text-[12px] text-[color:var(--muted)]">
            <div className="flex flex-wrap items-center gap-[8px]">
              <label className="flex items-center gap-[6px]">
                From
                <input type="datetime-local" className={FIELD} value={fieldOf(draft.from)} onChange={type("from")} data-date-input="from" />
              </label>
              <label className="flex items-center gap-[6px]">
                to
                <input type="datetime-local" className={FIELD} value={pending ? "" : fieldOf(draft.to)} onChange={type("to")} data-date-input="to" />
              </label>
            </div>
            <div className="ml-auto flex items-center gap-[8px]">
              <span className="tabular-nums" data-date-length="">{valid ? lengthOf(draft.from, draft.to) : ""}</span>
              <button type="button" onClick={close} className={QUIET} data-date-cancel="">Cancel</button>
              <button type="button" onClick={apply} disabled={!valid} className={PRIMARY} data-date-apply="">Apply</button>
            </div>
          </div>
        </div>,
        document.body,
      ) : null}
    </div>
  );
}
